import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { novels } from '@/mocks/novels';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const novel = novels.find((n) => n.id === id);

  const [activeTab, setActiveTab] = useState<'intro' | 'chapters'>('intro');

  if (!novel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <p className="text-foreground-500 text-lg mb-4">文章不存在</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedNovels = novels
    .filter((n) => n.id !== novel.id && (n.category === novel.category || n.tags.some((t) => novel.tags.includes(t))))
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: novel.title,
    description: novel.description,
    author: {
      '@type': 'Person',
      name: novel.author,
    },
    datePublished: novel.publishDate,
    url: `https://cbdti.cn/article/${novel.id}`,
    image: novel.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '嫂子的诱惑编辑部',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://cbdti.cn/article/${novel.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cbdti.cn/' },
      { '@type': 'ListItem', position: 2, name: novel.category, item: `https://cbdti.cn/category/${novel.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: novel.title },
    ],
  };

  return (
    <PageMeta
      title={`${novel.title} - 嫂子的诱惑 | ${novel.author}作品导读`}
      description={novel.description.slice(0, 160)}
      keywords={`${novel.title},${novel.tags.join(',')},${novel.category},都市情感小说,言情网络小说推荐`}
      canonicalUrl={`https://cbdti.cn/article/${novel.id}`}
      ogTitle={`${novel.title} - 嫂子的诱惑`}
      ogDescription={novel.description.slice(0, 160)}
      ogType="article"
      ogUrl={`https://cbdti.cn/article/${novel.id}`}
      twitterCard="summary_large_image"
      jsonLd={[articleSchema, breadcrumbSchema]}
    >
      <main className="min-h-screen bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: novel.category, href: `/category/${novel.categorySlug}` },
              { label: novel.title },
            ]}
          />

          <article className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <header className="mb-8">
                  <div className="flex items-center flex-wrap gap-2 mb-3">
                    <span className="text-xs px-3 py-1 bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                      {novel.category}
                    </span>
                    {novel.isHot && (
                      <span className="text-xs px-3 py-1 bg-accent-100 text-accent-800 rounded-full whitespace-nowrap">
                        热门推荐
                      </span>
                    )}
                    {novel.isRecommended && (
                      <span className="text-xs px-3 py-1 bg-secondary-100 text-secondary-800 rounded-full whitespace-nowrap">
                        编辑推荐
                      </span>
                    )}
                  </div>
                  <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4 leading-tight">
                    {novel.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i> {novel.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i> {novel.publishDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {(novel.views / 10000).toFixed(1)}万阅读
                    </span>
                    <span className="flex items-center gap-1 text-accent-600">
                      <i className="ri-star-fill"></i> {novel.rating}
                    </span>
                    <span className="px-2 py-0.5 bg-background-100 rounded text-xs border border-background-200/70 whitespace-nowrap">
                      {novel.status}
                    </span>
                    <span className="px-2 py-0.5 bg-background-100 rounded text-xs border border-background-200/70 whitespace-nowrap">
                      {novel.wordCount}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {novel.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-3 py-1 bg-background-100 text-foreground-600 rounded-full border border-background-200/70 whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </header>

                <div className="flex items-center gap-1 bg-background-100 border border-background-200/70 rounded-lg p-1 mb-8 w-fit">
                  <button
                    onClick={() => setActiveTab('intro')}
                    className={`px-6 py-2 text-sm rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeTab === 'intro'
                        ? 'bg-primary-500 text-background-50 font-medium'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    作品导读
                  </button>
                  <button
                    onClick={() => setActiveTab('chapters')}
                    className={`px-6 py-2 text-sm rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeTab === 'chapters'
                        ? 'bg-primary-500 text-background-50 font-medium'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    章节预览
                  </button>
                </div>

                {activeTab === 'intro' ? (
                  <section
                    className="prose prose-sm max-w-none text-foreground-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: novel.content }}
                  />
                ) : (
                  <section>
                    <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 mb-4">
                      <h3 className="font-heading text-lg text-foreground-900 mb-2">第一章</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {novel.description}
                      </p>
                      <p className="text-sm text-foreground-400 mt-4 italic">
                        ... 更多精彩内容，敬请关注更新 ...
                      </p>
                    </div>
                    <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 mb-4">
                      <h3 className="font-heading text-lg text-foreground-900 mb-2">第二章</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        命运的齿轮悄然转动，谁也无法预料下一刻会发生什么。在这个繁华都市的某个角落，一段被禁忌的情感正在悄然滋长……
                      </p>
                    </div>
                    <p className="text-sm text-foreground-400 text-center mt-6">
                      更多章节持续更新中，请关注我们的最新动态
                    </p>
                  </section>
                )}

                <div className="flex items-center gap-4 mt-10 pt-6 border-t border-background-200/70">
                  <span className="text-sm text-foreground-500">分享本文：</span>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </button>
                </div>
              </div>

              <aside className="lg:col-span-1">
                <div className="sticky top-24">
                  <div className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden mb-6">
                    <img
                      src={novel.coverImage}
                      alt={novel.title + ' - 小说封面'}
                      title={novel.title + ' - ' + novel.author + '作品'}
                      className="w-full aspect-[3/4] object-cover object-top"
                    />
                  </div>

                  <div className="bg-background-100 rounded-lg border border-background-200/70 p-5 mb-6">
                    <h3 className="font-heading text-base text-foreground-900 mb-3">作品信息</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-foreground-500">作者</span>
                        <span className="text-foreground-800">{novel.author}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-500">分类</span>
                        <Link to={`/category/${novel.categorySlug}`} className="text-primary-600 hover:text-primary-700 transition-colors">
                          {novel.category}
                        </Link>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-500">状态</span>
                        <span className="text-foreground-800">{novel.status}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-500">字数</span>
                        <span className="text-foreground-800">{novel.wordCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-500">评分</span>
                        <span className="text-accent-600 font-medium">{novel.rating}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-500">阅读量</span>
                        <span className="text-foreground-800">{(novel.views / 10000).toFixed(1)}万</span>
                      </div>
                    </div>
                  </div>

                  {relatedNovels.length > 0 && (
                    <div className="bg-background-100 rounded-lg border border-background-200/70 p-5">
                      <h3 className="font-heading text-base text-foreground-900 mb-4">相关推荐</h3>
                      <div className="space-y-4">
                        {relatedNovels.map((rn) => (
                          <Link
                            key={rn.id}
                            to={`/article/${rn.id}`}
                            className="flex gap-3 group cursor-pointer"
                          >
                            <img
                              src={rn.coverImage}
                              alt={rn.title}
                              className="w-14 h-20 object-cover rounded flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                                {rn.title}
                              </h4>
                              <p className="text-xs text-foreground-500 mt-1">{rn.author}</p>
                              <p className="text-xs text-accent-600 mt-0.5">
                                <i className="ri-star-fill text-xs"></i> {rn.rating}
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </article>
        </div>
      </main>
    </PageMeta>
  );
}